
import { defineComponent } from 'vue';
import { addToState, filterItems, fitSquaresInRect, getTwConfig, removeFromState } from '@/utils';
import BizIcon from '@/components/base/BizIcon.vue';
import { PRODUCTS_REST_ENDPOINT } from '@/bizerba';

import {
    foodIndustryKeys,
    logisticsKeys,
    manufacturingKeys,
    retailKeys,
    foodcraftsKeys,
    gastronomyKeys
} from '@/utils/categoryKeys';

const LIMIT = 4;
const CAT_GRID_LIMIT = 6;
const CAT_GRID_ICON_LIMIT = 18;

export default defineComponent({
    components: { BizIcon },
    props: {
        uuid: String,
        image: String,
        headlineLabel: String,
        resultsHeadlineLabel: String,
        backLabel: String,
        nextLabel: String,
        showAllLabel: String
    },
    data() {
        return {
            ready: false,
            columns: null,
            filters: [],
            selectedCategories: [],
            step: 0,
            nextStepCategories: [],
            offset: 0,
            cellSize: 0,
            currentImage: null,
            result: null,
            twConfig: getTwConfig(),
            gridSize: {},
            CAT_GRID_LIMIT,
            CAT_GRID_ICON_LIMIT
        };
    },
    provide() {
        return {
            registerOverviewFilter: this.registerFilter,
            toggleOverviewFilter: () => undefined,
            overviewItems: []
        };
    },
    mounted() {
        this.getCategories();
        window.addEventListener('resize', this.onResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        registerFilter(filter) {
            if (filter.hero) {
                this.columns = filter;
            } else {
                this.filters.push(filter);
                this.selectedCategories.push([]);
            }
            if (
                this.$slots.default().filter(filterItems).length ===
                this.filters.length + (this.columns ? 1 : 0)
            ) {
                this.ready = true;
                this.loadHash();
            }
        },
        selectStep(step) {
            if (step < this.step) {
                for (let i = 0; i < this.selectedCategories.length; i++) {
                    if (i > step) {
                        this.selectedCategories[i] = [];
                    }
                }
            }
            this.step = step;
        },
        next() {
            if (this.step < this.filters.length) {
                this.step++;
            }
        },
        prev() {
            if (this.step > 0) {
                removeFromState(this.currentCategory.filterKey);
                for (let i = this.step; i < this.selectedCategories.length; i++) {
                    this.selectedCategories[i] = [];
                }
                this.step--;
            }
        },
        toggleCategory(key) {
            const index = this.selectedCategories[this.step].indexOf(key);
            if (index < 0) {
                // at the first step, is only possible to select one element
                if (this.step === 0) {
                    this.selectedCategories[0].forEach(element => {
                        removeFromState(this.currentCategory.filterKey, element);
                        this.selectedCategories[this.step].splice(0, 1);
                    });
                }
                addToState(this.currentCategory.filterKey, key);
                this.selectedCategories[this.step].push(key);
            } else {
                removeFromState(this.currentCategory.filterKey, key);
                this.selectedCategories[this.step].splice(index, 1);
            }
        },
        isCategorySelected(key) {
            return this.selectedCategories[this.step].some(x => x === key);
        },

        // get  all possible categories
        async getCategories() {
            if (this.step >= this.filters.length) {
                return;
            }
            let url = `${this.$contextPath}${PRODUCTS_REST_ENDPOINT}/${this.$siteName}/${this.$lang}/${this.uuid}/${this.step}`;
            if (this.filterString.length) {
                url += `?filter=${this.filterString}`;
            }
            if (this.$region) {
                url += `&region=${this.$region}`;
            }
            try {
                const response = await fetch(url);
                const allCategories = await response.json();
                // this.nextStepCategories = allCategories.slice(0, 18);
                this.nextStepCategories = allCategories;
            } catch (e) {
                console.error(e);
            }
            this.$nextTick(() => {
                if (this.currentCategories.length > CAT_GRID_LIMIT) {
                    this.calcGrid();
                }
            });
        },
        async loadAll(column) {
            const results = await this.getProductColumn(column, -1);
            this.result[results.key] = results.result;
        },
        getProducts() {
            Promise.all(this.columns.categories.map(x => this.getProductColumn(x))).then(
                results => {
                    this.result = {};
                    results.forEach(({ key, result }) => {
                        this.result[key] = result;
                    });
                }
            );
        },
        async getProductColumn(column, limit = LIMIT) {
            let url = `${this.$contextPath}${PRODUCTS_REST_ENDPOINT}/${this.$siteName}/${this.$lang}/${this.uuid}/products/${column.categoryId}?limit=${limit}&offset=${this.offset}`;
            if (this.filterString.length) {
                url += `&filter=${this.filterString}`;
            }
            if (this.$region) {
                url += `&region=${this.$region}`;
            }
            try {
                const response = await fetch(url);
                const result = await response.json();
                return { key: column.categoryKey, result };
            } catch (e) {
                console.error(e);
                return null;
            }
        },
        calcGrid() {
            const rect: DOMRect = this.$refs.catContainer.getBoundingClientRect();
            this.cellSize =
                fitSquaresInRect(rect.width, rect.height, this.currentCategories.length) - 16; // remove gap
            this.gridSize =
                window.innerWidth >= this.breakpointSize('lg') && this.currentCategories.length > 6
                    ? { width: `${this.cellSize}px` }
                    : {};
        },
        hoverCategory(category) {
            this.currentImage = category.image || null;
        },
        blurCategory() {
            if (this.selectedCategories[this.step].length) {
                const categoryKey =
                    this.selectedCategories[this.step][
                        this.selectedCategories[this.step].length - 1
                    ];
                const category = this.currentCategories.find(x => x.categoryKey === categoryKey);
                this.currentImage = category.image || null;
            } else {
                this.currentImage = null;
            }
        },
        breakpointSize(name: string): number {
            if (Object.prototype.hasOwnProperty.call(this.twConfig.theme.screens, name)) {
                return parseInt(this.twConfig.theme.screens[name]);
            }
            return -1;
        },
        loadHash() {
            const hash = location.hash.replace(/^#/, '');
            const filters = hash
                .split('&')
                .filter(x => x.includes(':'))
                .map(x => x.split(':'))
                .filter(x => x.length === 2);
            if (
                this.filters.length >= filters.length &&
                filters.every((x, i) => this.filters[i].filterKey === x[0])
            ) {
                filters.forEach((x, i) => {
                    this.selectedCategories[i] = x[1].split(',');
                });
                this.selectStep(filters.length);
            }
        },
        onResize() {
            // recalculate grid size on resize and if is visible and bigger than the limit
            this.$nextTick(() => {
                if (this.$refs.catContainer) {
                    this.calcGrid();
                } else {
                    this.gridSize = {};
                }
            });
        }
    },
    computed: {
        filterString() {
            return this.selectedCategories
                .map((x, i) => (x.length ? `${this.filters[i].filterKey}:${x.join(',')}` : ''))
                .filter(x => x.length)
                .join(';');
        },
        currentCategory() {
            if (this.filters.length > this.step) {
                return this.filters[this.step];
            }
            return this.filters.length ? this.filters[this.filters.length - 1] : null;
        },
        currentCategories() {
            if (this.currentCategory) {
                let categories = this.currentCategory.categories;
                if (this.step === 1) {
                    // select categories available for the industry
                    switch (this.selectedCategories[0][0]) {
                        case 'food-industry':
                            categories = this.currentCategory.categories.filter(category =>
                                foodIndustryKeys.includes(category.categoryKey)
                            );
                            break;
                        case 'logistics':
                            categories = this.currentCategory.categories.filter(category =>
                                logisticsKeys.includes(category.categoryKey)
                            );
                            break;
                        case 'manufacturing-process-industry':
                            categories = this.currentCategory.categories.filter(category =>
                                manufacturingKeys.includes(category.categoryKey)
                            );
                            break;
                        case 'retail':
                            categories = this.currentCategory.categories.filter(category =>
                                retailKeys.includes(category.categoryKey)
                            );
                            break;
                        case 'food-crafts':
                            categories = this.currentCategory.categories.filter(category =>
                                foodcraftsKeys.includes(category.categoryKey)
                            );
                            break;
                        case 'gastronomy':
                            categories = this.currentCategory.categories.filter(category =>
                                gastronomyKeys.includes(category.categoryKey)
                            );
                            break;
                    }
                }
                return this.step >= 0
                    ? categories.filter(c => this.nextStepCategories.some(x => x === c.categoryId))
                    : categories;
            }
            return [];
        }
    },
    watch: {
        step() {
            if (this.step >= 0) {
                this.step === this.filters.length ? this.getProducts() : this.getCategories();
            }
            if (this.step > 1) {
                this.$refs.steps.scrollLeft = this.$refs.steps.scrollWidth;
            }
            this.onResize();
        }
    }
});
